/**
 * Toast — a minimal fixed bottom-right notification (DMS docs-fix item 3).
 *
 * The Ablage screens already had TWO inline bottom-right toast blocks (a green
 * `feedback` + a red `errorFeedback`, both plain-string only). This factors that
 * exact chrome into ONE reusable component that additionally supports an optional
 * inline ACTION link — the dedup toast needs "… (with a link to Admin → Duplikate)".
 *
 * Tone tokens mirror the pre-existing inline markup verbatim:
 *   - `ok`    → sem-ok    (green)  — success/info
 *   - `alert` → sem-alert (red)    — the "Duplikat erkannt" dedup toast
 *
 * Presentational only: the parent owns show/auto-dismiss timing (`useState` +
 * `setTimeout`, the established DocumentsScreen pattern) and passes `onDismiss`.
 */
import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '../../lib/cn';

export type ToastTone = 'ok' | 'alert';

const TONE_CLASSES: Record<ToastTone, string> = {
  ok: 'border-sem-ok/40 bg-sem-ok-bg text-sem-ok',
  alert: 'border-sem-alert/40 bg-sem-alert-bg text-sem-alert',
};

export interface ToastProps {
  tone: ToastTone;
  /** The message body. */
  children: ReactNode;
  /** Optional inline action rendered as an underlined link/button after the message. */
  action?: { label: string; onClick: () => void };
  /** Optional dismiss (renders an X). When omitted no close button shows (auto-dismiss only). */
  onDismiss?: () => void;
  /** Optional leading icon. */
  icon?: ReactNode;
}

export function Toast({ tone, children, action, onDismiss, icon }: ToastProps) {
  return (
    <div
      role={tone === 'alert' ? 'alert' : 'status'}
      aria-live={tone === 'alert' ? 'assertive' : 'polite'}
      className={cn(
        'fixed bottom-4 right-4 z-50 flex max-w-sm items-center gap-2 rounded-lg border px-4 py-2.5 text-sm shadow-md',
        TONE_CLASSES[tone],
      )}
    >
      {icon && <span className="shrink-0">{icon}</span>}
      <span className="min-w-0">{children}</span>
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          className="shrink-0 whitespace-nowrap font-medium underline underline-offset-2 hover:no-underline"
        >
          {action.label}
        </button>
      )}
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Schliessen"
          className="ml-1 shrink-0 opacity-70 hover:opacity-100"
        >
          <X className="size-3.5" />
        </button>
      )}
    </div>
  );
}
